import { RefObject, useLayoutEffect, useState } from 'react';

/**
 * How much to shrink an SVG `<text>` so it fits `maxWidth` (in the SVG's user units): 1 when it already fits, less
 * when it does not, never more. Measured after layout and again once web fonts finish loading, and re-measured when
 * `content` changes. Where SVG text cannot be measured (jsdom), the scale stays 1.
 */
export const useFitTextScale = (ref: RefObject<SVGTextElement>, maxWidth: number, content: unknown): number => {
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || typeof element.getComputedTextLength !== 'function') return undefined;
    let cancelled = false;

    const measure = () => {
      if (cancelled) return;
      // Measure at full size so the result does not depend on the scale already applied
      const previous = element.style.fontSize;
      element.style.fontSize = '';
      const width = element.getComputedTextLength();
      element.style.fontSize = previous;
      const next = width > maxWidth && width > 0 ? maxWidth / width : 1;
      setScale((current) => (Math.abs(current - next) > 0.005 ? next : current));
    };

    measure();
    document.fonts?.ready.then(measure).catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [ref, maxWidth, content]);

  return scale;
};
